import { useEffect } from 'react'

import { useAtom, useSetAtom } from 'jotai'

import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { AutomationsPresentationV1 } from 'Automations/automationEditor/types'
import {
  automationNameAtom,
  lastSavedPresentationAtom,
  lastSavedNameAtom,
  lastSavedDescriptionAtom,
  isAutomationActiveAtom,
  presentationAtom,
  resetPresentationHistoryAtom,
  selectedNodeIdsAtom,
  editingVersionAtom,
  activeVersionAtom,
  automationDescriptionAtom,
  usePopulateResolvedNodeDefinitions
} from 'Automations/automationEditor/atoms'
import { createEmptyAutomationPresentation } from 'Automations/automationEditor/helpers'
import { getLatestVersion } from 'Automations/helpers'
import { useAutomationId } from '../useAutomationId'

export const useLoadAutomationEditor = () => {
  const { automationId, isNewAutomation } = useAutomationId()

  const [presentation, setPresentation] = useAtom(presentationAtom)
  const setAutomationName = useSetAtom(automationNameAtom)
  const setAutomationDescription = useSetAtom(automationDescriptionAtom)
  const setIsAutomationActive = useSetAtom(isAutomationActiveAtom)
  const setSelectedNodeIds = useSetAtom(selectedNodeIdsAtom)
  const setLastSavedPresentation = useSetAtom(lastSavedPresentationAtom)
  const setLastSavedName = useSetAtom(lastSavedNameAtom)
  const setLastSavedDescription = useSetAtom(lastSavedDescriptionAtom)

  const setEditingVersion = useSetAtom(editingVersionAtom)
  const setActiveVersion = useSetAtom(activeVersionAtom)
  const resetHistory = useSetAtom(resetPresentationHistoryAtom)

  // Resolve every node definition's async-derived data once, at load, into the
  // resolved registry the editor reads synchronously thereafter. The only fetch
  // (customer custom-property keys) is router-preloaded and cached, so this is
  // effectively instant; the editor shows its loading skeleton until it lands.
  const definitionsReady = usePopulateResolvedNodeDefinitions()

  const automationQuery = useQuery(
    dashboardv99990101Client.getAutomation,
    { id: automationId ?? '' },
    { enabled: !!automationId && !isNewAutomation }
  )

  const automationVersionsQuery = useQuery(
    dashboardv99990101Client.getAutomationVersions,
    { automationId: automationQuery.data?.id ?? '' },
    { enabled: !!automationQuery.data?.id && !isNewAutomation }
  )

  // The versions endpoint returns the active version first, not the most
  // recently created, so the editor opens on the latest version by createdAt.
  const latestAutomationVersion = getLatestVersion(
    automationVersionsQuery.data?.items ?? []
  )

  const activeAutomationVersion = automationVersionsQuery.data?.items.find(
    ({ active }) => active
  )

  useEffect(() => {
    if (isNewAutomation && !presentation) {
      const emptyPresentation = createEmptyAutomationPresentation()
      setPresentation(emptyPresentation)
      setLastSavedPresentation(emptyPresentation)
      setLastSavedName('')
      setLastSavedDescription('')
      resetHistory()

      // preselect start node
      setSelectedNodeIds([emptyPresentation.nodes[0].id])

      return
    }

    if (!latestAutomationVersion || !automationQuery.data || presentation) {
      return
    }

    setAutomationName(automationQuery.data.name)
    setAutomationDescription(automationQuery.data.description)
    setLastSavedName(automationQuery.data.name)
    setLastSavedDescription(automationQuery.data.description)
    setPresentation(
      latestAutomationVersion.presentation as AutomationsPresentationV1
    )
    setLastSavedPresentation(
      latestAutomationVersion.presentation as AutomationsPresentationV1
    )
    resetHistory()
    setEditingVersion(latestAutomationVersion)
    setActiveVersion(activeAutomationVersion)

    /**
     * DEPRECATED
     */
    setIsAutomationActive(latestAutomationVersion.active)
  }, [
    latestAutomationVersion,
    setPresentation,
    isNewAutomation,
    setAutomationName,
    setAutomationDescription,
    automationQuery.data,
    setIsAutomationActive,
    presentation,
    setSelectedNodeIds,
    setLastSavedPresentation,
    setLastSavedName,
    setLastSavedDescription,
    setEditingVersion,
    setActiveVersion,
    activeAutomationVersion,
    resetHistory
  ])

  // The editor reads node definitions from the resolved registry, so it isn't
  // ready until that registry is populated — gate the loading state on it.
  const isResolvingDefinitions = !definitionsReady

  if (isNewAutomation || presentation) {
    return {
      isLoading: isResolvingDefinitions,
      isError: false,
      isNotFound: false
    }
  }

  return {
    isLoading:
      isResolvingDefinitions ||
      automationQuery.isPending ||
      automationVersionsQuery.isPending,
    isError:
      (automationQuery.isError && automationQuery.error.status !== 404) ||
      automationVersionsQuery.isError,
    isNotFound: automationQuery.isError && automationQuery.error.status === 404
  }
}
